export const getCciCategory = (cci) => {
  const value = parseFloat(cci);
  if (isNaN(value) || value < 0) {
    return '';
  }
  if (value <= 0.5) return 'Very Low';
  if (value <= 1.0) return 'Low';
  if (value <= 1.5) return 'Low/Medium';
  if (value <= 2.0) return 'Medium';
  if (value <= 2.5) return 'Medium/High';
  if (value <= 3.0) return 'High';
  return 'Very High';
};

export const getComparisonColor = (value, comparisonValue, lowerIsBetter = true) => {
  const compVal = parseFloat(comparisonValue);
  if (isNaN(compVal)) {
    return 'text-gray-500';
  }

  const parsedValue = parseFloat(value);
  if (lowerIsBetter) {
    return parsedValue > compVal ? 'text-red-500' : 'text-green-500';
  }
  return parsedValue < compVal ? 'text-red-500' : 'text-green-500';
};

export const deriveSitesFromTreemap = (siteTreemapData, systemId) => {
  if (!Array.isArray(siteTreemapData) || !systemId) {
    return [];
  }

  return siteTreemapData
    .filter((node) => node.parent === systemId)
    .map((node) => ({
      id: node.id,
      name: node.id,
      value: node.value,
      percentage: node.color,
      colorValue: typeof node.color === 'number' ? node.color / 100 : 0,
      patientCount: node.value,
      type: node.id?.toLowerCase().includes('hospital') ? 'Hospital' : 'Clinic',
    }));
};

export const filterSitesByType = (sites, selectedSiteType) => {
  if (!Array.isArray(sites) || selectedSiteType === 'All') {
    return sites ?? [];
  }

  return sites.filter((site) => site.type === selectedSiteType);
};

export const calculateScaleValues = (sites) => {
  if (!Array.isArray(sites) || sites.length === 0) {
    return { min: 'N/A', mid: 'N/A', max: 'N/A' };
  }

  const colorValues = sites.map((site) => site.colorValue ?? 0);
  const minActualColorVal = Math.min(...colorValues);
  const maxActualColorVal = Math.max(...colorValues);
  const midActualColorVal =
    minActualColorVal === maxActualColorVal
      ? minActualColorVal
      : (minActualColorVal + maxActualColorVal) / 2;

  const toPercent = (value) => `${(value * 100).toFixed(1)}%`;

  return {
    min: toPercent(minActualColorVal),
    mid: toPercent(midActualColorVal),
    max: toPercent(maxActualColorVal),
  };
};

export const buildTreemapChartData = (selectedSystemName, sites) => {
  return [
    ['Site', 'Parent', 'Patient Value', 'Percentage'],
    [selectedSystemName, null, 0, 0],
    ...sites.map((site) => [
      site.name,
      selectedSystemName,
      site.value,
      site.colorValue,
    ]),
  ];
};

export const buildHospitalTooltipData = (sites, hospitalDistributionData) => {
  if (!Array.isArray(sites) || !hospitalDistributionData?.bins) {
    return {};
  }

  const tooltipData = {};
  const { bins } = hospitalDistributionData;

  bins.forEach((bin) => {
    tooltipData[bin] = [];
  });

  sites.forEach((site) => {
    const sitePercentage = parseFloat(site.percentage);
    if (isNaN(sitePercentage)) {
      return;
    }

    for (let i = 0; i < bins.length; i += 1) {
      const bin = bins[i];
      const binParts = bin.replace(/%/g, '').split('-');
      const lowerBound = parseFloat(binParts[0]);
      let upperBound = Number.POSITIVE_INFINITY;
      let inBin = false;

      if (bin.includes('+')) {
        inBin = sitePercentage >= lowerBound;
      } else if (binParts.length > 1) {
        upperBound = parseFloat(binParts[1]);
        if (sitePercentage >= lowerBound && sitePercentage < upperBound) {
          inBin = true;
        } else if (
          i === bins.length - 1 &&
          sitePercentage >= lowerBound &&
          sitePercentage <= upperBound
        ) {
          inBin = true;
        }
      }

      if (inBin) {
        tooltipData[bin].push({
          name: `${site.name} (${site.type || 'N/A'})`,
          patientCountForNeed: site.value,
          unmetNeedPercentage: site.percentage,
        });
        break;
      }
    }
  });

  Object.keys(tooltipData).forEach((bin) => {
    tooltipData[bin].sort((a, b) => b.patientCountForNeed - a.patientCountForNeed);
    tooltipData[bin] = tooltipData[bin].slice(0, 10);
  });

  return tooltipData;
};

export const noLabelsPlugin = {
  id: 'noLabels',
  beforeDraw: (chart) => {
    const averageDatasets = chart.data.datasets.filter(
      (dataset) => dataset.label === 'System Average' || dataset.label?.includes('Average')
    );

    averageDatasets.forEach((dataset) => {
      if (dataset.datalabels) {
        dataset.datalabels.display = false;
      }
    });
  },
};
